import {
  computeMetricDomains,
  domainPercent,
  rawMetricKey,
  type ChartDatum,
  type ResolvedMetric,
} from "../components/OverviewChart/chartModel.js";
import { formatTopicTitle } from "../components/OverviewChart/chartPrimitives.js";

export const CHART_CSV_MIME = "text/csv;charset=utf-8";

/** Header of the first column — matches the Table view's Concept column. */
const CONCEPT_COLUMN = "Concept";

/** Suffix marking the normalized column that sits beside each metric's raw value. */
const PERCENT_COLUMN_SUFFIX = "(% of range)";

/** Decimal places kept on the normalized percent, mirroring the chart's own precision. */
const PERCENT_DECIMALS = 1;

/**
 * Quote a field only when it would otherwise break the row, and collapse embedded
 * newlines — matching the Retool `downloadOVChartData` export this replaces, so a
 * file opened side-by-side with an old one parses identically.
 */
function escapeCsvField(value: string): string {
  const clean = value.replace(/[\n\r]/g, " ").trim();
  if (!clean.includes('"') && !clean.includes(",") && !clean.includes("\n")) return clean;
  return `"${clean.replace(/"/g, '""')}"`;
}

/** Unformatted number so spreadsheets can compute on it; empty cell for no-data. */
function numericCell(value: string | number | null | undefined): string {
  return typeof value === "number" ? String(value) : "";
}

function percentCell(raw: string | number | null | undefined, domain: [number, number]): string {
  const percent = domainPercent(typeof raw === "number" ? raw : null, domain);
  if (percent === null) return "";
  const factor = 10 ** PERCENT_DECIMALS;
  return String(Math.round(percent * factor) / factor);
}

/**
 * Serialize the charted rows — top-N concepts × selected metrics — as CSV.
 *
 * Each metric contributes two columns: its raw KPI value and where that value sits
 * within the metric's own axis range — i.e. the bar height the chart draws. Raw values
 * alone would drop the scaling the chart is read through; percents alone would drop the
 * underlying KPI, which is not recoverable from a percent (every metric has its own range).
 */
export function buildChartCsv(data: ChartDatum[], metrics: ResolvedMetric[]): string {
  const header = [
    CONCEPT_COLUMN,
    ...metrics.flatMap((m) => [m.label, `${m.label} ${PERCENT_COLUMN_SUFFIX}`]),
  ];

  const domains = computeMetricDomains(data, metrics);
  const rows = data.map((datum) => [
    formatTopicTitle(datum.category),
    ...metrics.flatMap((m) => [
      numericCell(datum[rawMetricKey(m.id)]),
      percentCell(datum[rawMetricKey(m.id)], domains[m.id]!),
    ]),
  ]);

  return [header, ...rows].map((row) => row.map(escapeCsvField).join(",")).join("\n");
}

/** `overview-chart-2026-08-02.csv` — dated so repeat exports don't overwrite each other. */
export function chartCsvFilename(now: Date = new Date()): string {
  return `overview-chart-${now.toISOString().slice(0, 10)}.csv`;
}
